import type { GeographicMarketTimeSeries as MarketDataTimeSeries } from '@einwert/valuation-api-client'
import type { Period } from '@einwert/valuation-api-client'
import {
    EmptyCell,
    ExhaustivenessError,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    MonetaryCell,
    MonetaryValueDataObjectType,
    NumberCell,
    PercentageCell,
    Table,
    TextCell,
} from 'ComponentLibrary'

import dayjs from 'dayjs'
import { createColumnHelper } from '@tanstack/react-table'
import { getTimeSeriesStatsTypeLabels } from 'ClientPortal/src/pages/MarketData/constants'
import { MarketDataTimeSeriesValue, MarketDataTimeSeriesValueType } from 'ClientPortal/src/pages/MarketData/types'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { useTranslation } from 'react-i18next'

type MarketDataTableProps = {
    valueType: MarketDataTimeSeries['value_type']
    series: MarketDataTimeSeries[]
}

export const MarketDataTable = ({ valueType, series }: MarketDataTableProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'marketData' })
    const locale = useCurrentResolvedLocale()
    const statsTypeLabels = getTimeSeriesStatsTypeLabels(t)
    const period = series[0].period
    const datesInSeries = Array.from(new Set(series.flatMap((serie) => serie.values.map((point) => point.date)))).toSorted()

    const mapValueTypeToCell = ({ valueType, value }: { valueType: MarketDataTimeSeriesValueType; value?: MarketDataTimeSeriesValue }) => {
        switch (valueType) {
            case 'monetary_value':
                return <MonetaryCell monetaryValue={value as MonetaryValueDataObjectType} locale={locale} />
            case 'percentage':
                return <PercentageCell percentage={value as number} locale={locale} />
            case 'money_per_area_value':
                return <MonetaryCell monetaryValue={value as MonetaryValueDataObjectType} additionalUnit="/m²" locale={locale} />
            case 'float':
            case 'integer':
                return <NumberCell number={value as number} isInteger={valueType === 'integer'} locale={locale} />
            default:
                return <EmptyCell />
        }
    }

    const columnHelper = createColumnHelper<MarketDataTimeSeries>()
    const columns = [
        columnHelper.accessor(
            (row) =>
                joinDefinedStrings([row.stats_type ? statsTypeLabels[row.stats_type] : row.title, row.origin && `(${row.origin.title})`]),
            {
                header: '',
                id: 'title',
                cell: (info) => <TextCell maxWidth="200px" text={info.getValue()} />,
            }
        ),
        ...datesInSeries.map((date) =>
            columnHelper.accessor((row) => row.values.findLast((point) => point.date === date), {
                header: formatDate(date, period, locale),
                cell: (info) =>
                    mapValueTypeToCell({
                        valueType,
                        value: info.getValue()?.value,
                    }),
            })
        ),
    ]

    return <Table width="100%" alignHeader="end" columns={columns} data={series.toReversed()} />
}

const formatDate = (date: string, period: Period, locale: string) => {
    const localizedDate = dayjs(date).locale(locale)

    switch (period) {
        case 'day':
            return isoDateStringToLocalFormat(date, false, locale) ?? ''
        case 'week':
            return localizedDate.format(locale === 'en-US' ? '[CW] ww YYYY' : '[KW] ww YYYY')
        case 'month':
            return isoDateStringToLocalizedMonthYear(date, 'numeric', locale) ?? ''
        case 'quarter':
            return localizedDate.format('[Q]Q YYYY')
        case 'year':
            return localizedDate.format('YYYY')
        default:
            throw ExhaustivenessError(period)
    }
}
